/* eslint-disable react/jsx-no-constructed-context-values */
import * as React from 'react';
import { Provider } from 'react-redux';
import { ErrorLogComponent } from '@cian/error-log-component';
import { DynamicCalltrackingContext } from '@cian/frontend-dynamic-calltracking-component';
import { Provider as MicrofrontendProvider } from '@cian/mf-react';
import { UiKitRoot } from '@cian/ui-kit';
import classNames from 'clsx';

import { CarouselOverlayPanel } from './components/CarouselOverlayPanel';
import { FixedHeader } from './components/FixedHeader';
import { Layout } from './components/Layout';
import { LayoutBlock } from './components/LayoutBlock';
import { AboutContainer } from './containers/About';
import { AdvantagesContainer } from './containers/Advantages';
import { AerialViewContainer } from './containers/AerialView';
import { BasicInfoContainer } from './containers/BasicInfo';
import { BuilderCallReasonsContainer } from './containers/BuilderCallReasons';
import { BreadCrumbsContainer } from './containers/BreadCrumbs';
import { BuildersInfoContainer } from './containers/BuildersInfo';
import { CallbackPopupContainer } from './containers/CallbackPopup';
import { CarouselContainer } from './containers/Carousel';
import { DocumentationContainer } from './containers/Documentation';
import { EscrowContainer } from './containers/Escrow';
import { FeaturesContainer } from './containers/Features';
import { FlatViewOrderTooltipContainer } from './containers/FlatViewOrderTooltip';
import { ImageSources } from './containers/ImageSources';
import { InteriorDecorationContainer } from './containers/InteriorDecoration';
import { LabelsContainer } from './containers/Labels';
import { LastHopePopupContainer } from './containers/LastHopePopup';
import { MortgageSectionContainer } from './containers/MortgageSection';
import { MVKOrMapContainer } from './containers/MVKOrMap';
import { NewbuildingChatContainer } from './containers/NewbuildingChat';
import { NewbuildingFromTheBuilderContainer } from './containers/NewbuildingFromTheBuilder';
import { NewbuildingReviewsTeaserContainer } from './containers/NewbuildingReviewsTeaser';
import { OffersBlockContainer } from './containers/OffersBlock';
import { PromoDetailsContainer } from './containers/PromoDetails';
import { PromosSectionContainer } from './containers/PromosSection';
import { RealtyValuationContainer } from './containers/RealtyValuation';
import { RecommendedNewbuildingsContainer } from './containers/RecommendedNewbuildings';
import { ReliableBlockContainer } from './containers/ReliableBlock';
import { RightColumnContainer } from './containers/RightColumn';
import { SpecificationsDetailedContainer } from './containers/SpecificationsDetailed';
import { FloorPlanLoadable } from './containers/FloorPlan/FloorPlanLoadable';
import { GenplanLoadable } from './containers/Genplan/GenplanLoadable';
import { IApplicationContext } from './types/applicationContext';
import { TReduxStore } from './types/redux';
import { ApplicationContext } from './utils/applicationContext';
import { setRegionCookies } from './utils/businessLogic/geo';
import { displayConsultantPhone } from './utils/newbuildingConsultant';
import { GenplanNavContainer } from './components/Genplan/components/GenplanNavContainer';

import * as style from './App.css';
import { OffersStatsContainer } from './containers/OffersStats';
import { CompanySeoCard } from './containers/CompanySeoCard';

export interface IApplicationProps extends IApplicationContext {
  reduxStore: TReduxStore;
}

export class Application extends React.Component<IApplicationProps> {
  public componentDidMount(): void {
    const state = this.props.reduxStore.getState();
    const { newbuilding } = state;
    setRegionCookies(newbuilding.geo.regionId as number, newbuilding.geo.mainTownId);

    const consultantPhone = this.props.config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions =
      this.props.config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];
    displayConsultantPhone(state, consultantPhoneEnabledRegions, consultantPhone);
  }

  public render() {
    const { logger, reduxStore, httpApi, config, microfrontendsRegistry } = this.props;

    return (
      <ErrorLogComponent logger={logger}>
        <ApplicationContext.Provider value={{ config, logger, httpApi, microfrontendsRegistry }}>
          <UiKitRoot>
            <DynamicCalltrackingContext.Provider value={{ logger, httpApi }}>
              <Provider store={reduxStore}>
                <MicrofrontendProvider registry={microfrontendsRegistry}>
                  <main className={style['app']}>
                    <FixedHeader />
                    <BreadCrumbsContainer />
                    <CarouselOverlayPanel />
                    <CarouselContainer />
                    <FlatViewOrderTooltipContainer />
                    <Layout>
                      <LayoutBlock>
                        <div className={classNames(style['content'], style['content--top'])}>
                          <div className={style['leftColumn']}>
                            <LabelsContainer />
                            <BasicInfoContainer />
                            <BuilderCallReasonsContainer />
                            <GenplanNavContainer>
                              <GenplanLoadable />
                            </GenplanNavContainer>
                            <FloorPlanLoadable />
                            <AerialViewContainer />
                            <OffersBlockContainer />
                            <PromosSectionContainer />
                            <MortgageSectionContainer />
                            <RealtyValuationContainer />
                            <AboutContainer />
                            <AdvantagesContainer />
                            <NewbuildingReviewsTeaserContainer />
                          </div>
                          <aside className={style['rightColumn']}>
                            <RightColumnContainer dynamicCalltrackingPlaceType="blockStickyRightTop" />
                          </aside>
                        </div>
                      </LayoutBlock>
                      <LayoutBlock>
                        <MVKOrMapContainer />
                      </LayoutBlock>
                      <LayoutBlock>
                        <div className={style['content']}>
                          <div className={style['leftColumn']}>
                            <SpecificationsDetailedContainer />
                            <InteriorDecorationContainer />
                            <BuildersInfoContainer />
                            <NewbuildingFromTheBuilderContainer />
                            <FeaturesContainer />
                            <ReliableBlockContainer />
                            <EscrowContainer />
                            <OffersStatsContainer />
                            <DocumentationContainer />
                            <RecommendedNewbuildingsContainer />
                          </div>
                          <aside className={style['rightColumn']}>
                            <RightColumnContainer dynamicCalltrackingPlaceType="blockStickyRightBottom" />
                          </aside>
                        </div>
                      </LayoutBlock>
                    </Layout>
                    <ImageSources />
                  </main>
                  <CallbackPopupContainer />
                  <NewbuildingChatContainer config={config} />
                  <LastHopePopupContainer />
                  <PromoDetailsContainer />
                  <CompanySeoCard />
                </MicrofrontendProvider>
              </Provider>
            </DynamicCalltrackingContext.Provider>
          </UiKitRoot>
        </ApplicationContext.Provider>
      </ErrorLogComponent>
    );
  }
}
